<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <Transition mode="out-in">
      <component :is="layout">
        <router-view />
      </component>
    </Transition>
    <scroll-to-top v-if="enableScrollToTop" />
  </div>
</template>

<script>
import ScrollToTop from "@core/components/scroll-to-top/ScrollToTop.vue";

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from "@themeConfig";
import { provideToast } from "vue-toastification/composition";
import { watch } from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";

import { useWindowSize, useCssVar } from "@vueuse/core";

import store from "@/store";

const LayoutVertical = () => import("@/layouts/vertical/LayoutVertical.vue");

const LayoutFull = () => import("@/layouts/full/LayoutFull.vue");

export default {
  components: {
    // Layouts
    LayoutVertical,
    LayoutFull,

    ScrollToTop,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (
        window &&
        window.location.pathname.includes("competitions") &&
        Object.keys(this.userData).length == 0
      ) {
        return "layout-full";
      }
      if (
        this.$route.meta.notNeedLogin &&
        Object.keys(this.userData).length == 0
      ) {
        return "layout-full";
      }
      if (this.$route.meta.layout === "full") return "layout-full";
      return `layout-${this.contentLayoutType}`;
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type;
    },
    userData() {
      return (
        this.$store.state.auth.userData ||
        JSON.parse(localStorage.getItem("userData"))
      );
    },
  },
  watch: {
    $route: {
      async handler(val) {
        if (val.query && val.query.notification) {
          const { message, type } = val.query.notification;
          this.$swal({
            icon: type == "success" ? "success" : "error",
            title: message,
          });
          this.$router.push({
            ...this.$route.params,
          });
        }
      },
      deep: true,
      immediate: true,
    },
  },
  // methods: {
  //   async getProfile() {
  //     try {
  //       const { data } = await this.$http.get("panel/profile/user");
  //       await localStorage.setItem(
  //         "userData",
  //         JSON.stringify({
  //           ...data.data,
  //           email_verified: data.email_verified,
  //           type:
  //             data.data.permissions &&
  //             data.data.permissions.permissions &&
  //             data.data.permissions.permissions.length
  //               ? "admin"
  //               : "user",
  //         })
  //       );
  //       await this.$store.dispatch("auth/setUserData", {
  //         ...data.data,
  //         email_verified: data.email_verified,
  //         type:
  //           data.data.permissions &&
  //           data.data.permissions.permissions &&
  //           data.data.permissions.permissions.length
  //             ? "admin"
  //             : "user",
  //       });
  //     } catch (err) {}
  //   },
  // },
  async beforeCreate() {
    if (localStorage.getItem("userData")) {
      await this.$store.dispatch(
        "auth/setUserData",
        JSON.parse(localStorage.getItem("userData"))
      );
    }
    // Set colors in theme
    const colors = [
      "primary",
      "secondary",
      "success",
      "info",
      "warning",
      "danger",
      "light",
      "dark",
    ];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        document.documentElement
      ).value.trim();
    }

    // Set Theme Breakpoints
    const breakpoints = ["xs", "sm", "md", "lg", "xl"];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement
        ).value.slice(0, -2)
      );
    }
    if (JSON.parse(localStorage.getItem("token"))) {
      this.$http.defaults.headers.common["Authorization"] = await `${
        JSON.parse(localStorage.getItem("token")).type
      } ${JSON.parse(localStorage.getItem("token")).val}`;
    }
    // Set RTL
    const { isRTL } = $themeConfig.layout;
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
  },
  setup() {
    const { skin, skinClasses } = useAppConfig();
    const { enableScrollToTop } = $themeConfig.layout;

    // If skin is dark when initialized => Add class to body
    if (skin.value === "dark") document.body.classList.add("dark-layout");

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: "Vue-Toastification__fade",
    });

    // Set Window Width in store
    store.commit("app/UPDATE_WINDOW_WIDTH", window.innerWidth);
    const { width: windowWidth } = useWindowSize();
    watch(windowWidth, (val) => {
      store.commit("app/UPDATE_WINDOW_WIDTH", val);
    });

    return {
      skinClasses,
      enableScrollToTop,
    };
  },
};
</script>
<style scoped>
.v-enter-active,
.v-leave-active {
  transition: all 0.4s ease;
}

.v-enter-from,
.v-leave-to {
  transform: scale(1.1);
  opacity: 0;
}
</style>
